import React from 'react';

import Card from 'components/Card';
import { BookingQuery, CreateAmadeusBookingMutation } from 'generated/graphql';
import { useAppSelector } from 'store/store';
import { formatCents, getPricingInfo, PLUS_TAX_TEXT } from 'utilities/money';
import { getControlFieldLabelForPod } from 'utilities/podBasedFiltering';

import { PaymentMethodDetails } from './PaymentMethodDetails';

interface Props {
  booking:
    | CreateAmadeusBookingMutation['createAmadeusBooking']
    | BookingQuery['booking'];
}

const InvoiceCard: React.FC<Props> = ({ booking }) => {
  // Extract pricing information using the utility function
  const { nightlyRate } = getPricingInfo(booking);
  const { pod: clientPod } = useAppSelector((state) => state.clientConfig);
  const showTravelerPayments = useAppSelector(
    (state) => state.enums.featureFlags?.flags.hotelSavedCards === true,
  );

  const subtotal = formatCents(booking.basePrice);
  const estimatedTax = formatCents(booking.estimatedTax);
  const totalPaid = formatCents(booking.grandTotalAfterTax);

  // Get line items if available (server already includes them in grandTotalAfterTax)
  const lineItems = 'lineItems' in booking ? booking.lineItems : [];
  const lineItemsSubtotal = lineItems.reduce(
    (total, item) => total + item.price,
    0,
  );

  return (
    <Card className="h-auto">
      <h2 className="mb-4 text-base font-bold">Invoice</h2>

      {/* Price Summary */}
      <div className="space-y-[10px] border-b border-gray-200 pb-5">
        <h3 className="font-arimo text-sm font-bold text-primaryGray">
          Price Summary
        </h3>

        {/* Per Room Pricing Section */}
        <div className="space-y-3 pt-3">
          <h4 className="text-sm text-gray-500">Per room</h4>
          <div className="flex justify-between text-sm">
            <p>Nightly rate:</p>
            <p>
              {nightlyRate}{' '}
              <span className="text-xs text-gray-500">{PLUS_TAX_TEXT}</span>
            </p>
          </div>
          <div className="flex justify-between text-sm">
            <p>Subtotal:</p>
            <p>
              {subtotal}{' '}
              <span className="text-xs text-gray-500">{PLUS_TAX_TEXT}</span>
            </p>
          </div>
          <div className="text-xs text-gray-500">
            {nightlyRate} × {booking.nights} night
            {booking.nights > 1 ? 's' : ''}
          </div>
        </div>

        {/* Line Items Section */}
        {lineItems.length > 0 && (
          <div className="space-y-3 pt-3">
            <h4 className="text-sm text-gray-500">Additional Items</h4>
            {lineItems.map((item) => (
              <div key={item.id} className="flex justify-between text-sm">
                <div>
                  <p>{item.name}</p>
                  {item.attributedGuestName && (
                    <p className="text-xs text-gray-500">
                      {item.attributedGuestName}
                    </p>
                  )}
                  {item.description && (
                    <p className="text-xs text-gray-500">{item.description}</p>
                  )}
                </div>
                <p>{formatCents(item.price)}</p>
              </div>
            ))}
          </div>
        )}

        {/* Total Pricing Section */}
        <div className="space-y-3 pt-3">
          <h4 className="text-sm text-gray-500">
            Total ({booking.rooms} room{booking.rooms > 1 ? 's' : ''})
          </h4>
          {lineItems.length > 0 && (
            <div className="flex justify-between text-sm">
              <p>Additional Items:</p>
              <p>{formatCents(lineItemsSubtotal)}</p>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <p>Estimated tax:</p>
            <p>{estimatedTax}</p>
          </div>
          <div className="flex justify-between border-t border-gray-200 pt-2 text-sm font-bold text-gray-900">
            <p>Total due:</p>
            <p>{totalPaid}</p>
          </div>
          <div className="text-xs text-gray-500">
            {subtotal} × {booking.rooms} room{booking.rooms > 1 ? 's' : ''}
            {lineItems.length > 0 &&
              ` + ${formatCents(lineItemsSubtotal)} additional items`}{' '}
            + {estimatedTax} tax
          </div>
        </div>
      </div>

      {/* Booking Details */}
      <div className="mt-5 space-y-[10px]">
        <h3 className="font-arimo text-sm font-bold text-primaryGray">
          Details
        </h3>
        <div className="flex justify-between text-sm">
          <p>Hotel Confirmation:</p>
          <p>{booking.confirmationNumber}</p>
        </div>
        <div className="flex justify-between text-sm">
          <p>Booked on:</p>
          <p>{new Date(booking.createdAt).toLocaleDateString()}</p>
        </div>
        <div className="flex justify-between text-sm">
          <p>Booking Holder:</p>
          <div className="text-right">
            <p>
              {booking.bookingHolder.firstName} {booking.bookingHolder.lastName}
            </p>
            <p className="text-gray-500">{booking.bookingHolder.email}</p>
          </div>
        </div>
        <div className="flex justify-between text-sm">
          <p>{getControlFieldLabelForPod(clientPod)}:</p>
          <p>{booking.orderNumber || 'N/A'}</p>
        </div>
        {showTravelerPayments && (
          <PaymentMethodDetails
            travelerPaymentMethod={
              'travelerPaymentMethod' in booking
                ? booking.travelerPaymentMethod
                : null
            }
            guestPaymentMethods={
              'guestPaymentMethods' in booking
                ? booking.guestPaymentMethods
                : []
            }
          />
        )}
      </div>
    </Card>
  );
};

export default InvoiceCard;
